/**
 * StateAggregatedProjects — /states/aggregated-projects
 */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useStateContext } from "@/hooks/useStateContext";
import { RefreshCw } from "lucide-react";
import client from "@/api/client";
import { PageHeader, SectionCard, MetricCard, DataTable } from "@/components/ds";

const STATUS_BADGES = {
  draft:        "bg-slate-100 text-slate-600",
  in_progress:  "bg-blue-100 text-blue-700",
  review_ready: "bg-amber-100 text-amber-700",
  complete:     "bg-emerald-100 text-emerald-700",
  submitted:    "bg-teal-100 text-teal-700",
  verified:     "bg-emerald-200 text-emerald-800",
};

export default function StateAggregatedProjects() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { stateId, geography: geo, stateParams } = useStateContext();

  useEffect(() => {
    client.get(`/states/aggregated-projects${stateParams}`)
      .then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [geo]);

  if (loading) return (
    <div className="flex items-center justify-center min-h-[40vh]">
      <RefreshCw size={18} className="animate-spin text-teal-500" />
    </div>
  );

  const projects = data?.projects || [];

  return (
    <div className="p-6 lg:p-8 space-y-6">
      <PageHeader
        label="STATES WORKSPACE"
        title="Aggregated Projects & Actions"
        subtitle={`${geo} · ${data?.total_count || 0} projects`}
        backUrl="/states/overview"
        backLabel="State Overview"
      />

      <div className="grid grid-cols-2 gap-4">
        <MetricCard
          label="Reduction Potential"
          value={(data?.total_reduction_tco2e || 0).toFixed(0)}
          unit="tCO₂e/yr"
          sub="Pipeline aggregate"
          color="text-teal-700"
        />
        <MetricCard
          label="CAPEX Pipeline"
          value={`$${((data?.total_capex_usd || 0) / 1e6).toFixed(1)}M`}
          sub="Total indicative"
          color="text-amber-700"
        />
      </div>

      {projects.length === 0 ? (
        <SectionCard>
          <div className="py-10 text-center">
            <p className="text-sm font-semibold text-slate-600">No projects in {geo}</p>
            <p className="text-xs text-slate-400 mt-1">
              Projects are aggregated from DeCarbonIQ organizational data by geography.
            </p>
          </div>
        </SectionCard>
      ) : (
        <DataTable
          columns={[
            { label: "Project" },
            { label: "Sector" },
            { label: "Type" },
            { label: "Stage" },
            { label: "Reduction (tCO₂e/yr)", align: "right" },
            { label: "Status" },
          ]}
        >
          {projects.map((p) => (
            <tr key={p.project_id} className="hover:bg-slate-50 transition-colors">
              <td className="px-4 py-3 font-semibold text-sm text-slate-900">{p.name}</td>
              <td className="px-4 py-3 text-sm text-slate-500 capitalize">{p.sector || "—"}</td>
              <td className="px-4 py-3 text-sm text-slate-500 capitalize">{p.project_type?.replace(/_/g, " ") || "—"}</td>
              <td className="px-4 py-3 text-sm text-slate-500 capitalize">{p.stage || "—"}</td>
              <td className="px-4 py-3 text-right font-semibold text-sm text-teal-700 tabular-nums">
                {(p.reduction_tco2e || 0).toFixed(1)}
              </td>
              <td className="px-4 py-3">
                <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${STATUS_BADGES[p.status] || "bg-slate-100 text-slate-600"}`}>
                  {p.status}
                </span>
              </td>
            </tr>
          ))}
        </DataTable>
      )}
    </div>
  );
}
